import type { QueryClient } from '@tanstack/react-query';

import {
    BehaviorController_getOverview,
    BehaviorController_getRespiration,
    BehaviorController_getSwimSpeed,
    BehaviorController_getTilt,
} from '@/services/services';

import {
    mapOverviewResponseToDomain,
    mapRespirationResponseToDomain,
    mapSwimSpeedResponseToDomain,
    mapTiltResponseToDomain,
} from './mappers';
import { toBehaviorRangeQuery, toBehaviorScopeQuery } from './queryParams';
import type {
    BehaviorDateRangeParams,
    BehaviorScopeParams,
    OverviewData,
    RespirationData,
    SwimSpeedChartData,
    TiltChartData,
} from './types';

/**
 * Fetches behavior overview data imperatively
 * Use this when you need to fetch data outside of React's render cycle
 * (e.g., in event handlers, export functions)
 *
 * @param queryClient - React Query client instance
 * @param cageId - The cage ID to fetch overview data for
 * @param params - Optional fish group to scope the overview to
 * @returns Promise with mapped overview data
 */
export async function fetchBehaviorOverview(
    queryClient: QueryClient,
    cageId: number,
    params?: BehaviorScopeParams
): Promise<OverviewData> {
    const query = toBehaviorScopeQuery(params);
    const response = await queryClient.fetchQuery({
        queryKey: [BehaviorController_getOverview.key, cageId, query],
        queryFn: () => BehaviorController_getOverview(cageId, query),
    });

    return mapOverviewResponseToDomain(response.data);
}

/**
 * Fetches swim speed data imperatively
 * Use this when you need to fetch data outside of React's render cycle
 *
 * @param queryClient - React Query client instance
 * @param cageId - The cage ID to fetch swim speed data for
 * @param params - Optional date range parameters (from/to in YYYY-MM-DD format, showHourly flag)
 * @returns Promise with mapped swim speed chart data
 */
export async function fetchSwimSpeed(
    queryClient: QueryClient,
    cageId: number,
    params?: BehaviorDateRangeParams
): Promise<SwimSpeedChartData> {
    const query = toBehaviorRangeQuery(params);
    const response = await queryClient.fetchQuery({
        queryKey: [BehaviorController_getSwimSpeed.key, cageId, query],
        queryFn: () => BehaviorController_getSwimSpeed(cageId, query),
    });

    return mapSwimSpeedResponseToDomain(response.data);
}

/**
 * Fetches respiration data imperatively
 * Use this when you need to fetch data outside of React's render cycle
 *
 * @param queryClient - React Query client instance
 * @param cageId - The cage ID to fetch respiration data for
 * @param params - Optional date range parameters (from/to in YYYY-MM-DD format, showHourly flag)
 * @returns Promise with mapped respiration data
 */
export async function fetchRespiration(
    queryClient: QueryClient,
    cageId: number,
    params?: BehaviorDateRangeParams
): Promise<RespirationData> {
    const query = toBehaviorRangeQuery(params);
    const response = await queryClient.fetchQuery({
        queryKey: [BehaviorController_getRespiration.key, cageId, query],
        queryFn: () => BehaviorController_getRespiration(cageId, query),
    });

    return mapRespirationResponseToDomain(response.data);
}

/**
 * Fetches tilt data imperatively
 * Use this when you need to fetch data outside of React's render cycle
 *
 * @param queryClient - React Query client instance
 * @param cageId - The cage ID to fetch tilt data for
 * @param params - Optional date range parameters (from/to in YYYY-MM-DD format)
 * @returns Promise with mapped tilt chart data
 */
export async function fetchTilt(
    queryClient: QueryClient,
    cageId: number,
    params?: BehaviorDateRangeParams
): Promise<TiltChartData> {
    const query = toBehaviorRangeQuery(params);
    const response = await queryClient.fetchQuery({
        queryKey: [BehaviorController_getTilt.key, cageId, query],
        queryFn: () => BehaviorController_getTilt(cageId, query),
    });

    return mapTiltResponseToDomain(response.data);
}
